import React, { FC, ReactNode, useMemo } from "react";
import { Form } from "react-final-form";
import { FieldInput } from "../../../fields/FieldInput";
import { FieldSelect } from "../../../fields/FieldSelect";
import { FieldCheckbox } from "../../../fields/FieldCheckbox";
import { Button } from "../../../Button";
import { EmailOptIn } from "../../../terms/EmailOptIn";
import { SignupValueProps } from "../ValueProps";
import {
  getSortedTermsByLocation,
  TermsAndConditions,
} from "../../../terms/TermsAndConditions";
import { PasswordInput } from "../../../fields/PasswordInput";
import { PasswordRequirement } from "../../../fields/PasswordInput/PasswordRequirements";
import {
  validateWithYup,
  getSignupWithAgeSchema,
  getSignupSchema,
} from "../../../../helpers/validation";
import {
  BrandConfigCustomizationsAccountValueprops,
  FormsSignupLocalization,
  FormsTosLocalization,
  FormsValidationLocalization,
  CustomText,
  PortalPasswordLocalization,
} from "../../../../types/graphql/generated";
import { formFields } from "../../../../constants";
import { SignupFormValues } from "../../../../types";
import {
  StyledColumn,
  StyledDivider,
  StyledError,
  StyledFields,
  StyledForm,
  StyledFormRow,
  StyledButtonWrapper,
  StyledBirthdayRow,
} from "./index.styled";
import { StyledLabel } from "../../../fields/base.styled";

interface SignupFormProps {
  accountValueProps?: BrandConfigCustomizationsAccountValueprops[];
  ageGate?: number | null;
  error: ReactNode;
  handlePasswordChange: (password: string) => Promise<PasswordRequirement[]>;
  customText: CustomText[];
  loading?: boolean;
  onSubmit: (formValues: SignupFormValues) => void;
  passwordLocalization?: PortalPasswordLocalization | null;
  signupLocalization?: FormsSignupLocalization | null;
  tosLocalization?: FormsTosLocalization | null;
  validationLocalization?: FormsValidationLocalization | null;
}

const getOptionsHelper = (
  startNum: number,
  endNum: number,
  isDays: boolean
) => {
  const final = [];
  for (let i = startNum; i < endNum; i++) {
    const value = isDays
      ? i < 10
        ? "0" + i.toString()
        : i.toString()
      : i.toString();
    final.push({ value, label: i.toString() });
  }
  return final;
};

const MonthsOptions = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

export const SignupForm: FC<SignupFormProps> = ({
  accountValueProps,
  ageGate,
  error,
  handlePasswordChange,
  customText,
  loading = false,
  onSubmit,
  passwordLocalization,
  signupLocalization,
  tosLocalization,
  validationLocalization,
}) => {
  const {
    firstNameLabelText = "First Name*",
    lastNameLabelText = "Last Name*",
    emailLabelText = "Email*",
    birthdayLabelText = "Birthday*",
    monthLabelText = "Month",
    dayLabelText = "Day",
    yearLabelText = "Year",
    passwordLabelText = "Password*",
    submitText = "Create your account",
  } = signupLocalization || {};

  const daysOptions = useMemo(() => {
    return getOptionsHelper(1, 32, true);
  }, []);

  const yearsOptions = useMemo(() => {
    return getOptionsHelper(1900, 2022, false).reverse();
  }, []);

  const initialValues = {
    emailOptIn: false,
    ...(!!ageGate && {
      month: "",
      day: "",
      year: "",
    }),
  };

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      validate={validateWithYup(
        ageGate
          ? getSignupWithAgeSchema(validationLocalization)
          : getSignupSchema(validationLocalization)
      )}
      render={({ handleSubmit, submitFailed, values }) => (
        <StyledForm onSubmit={handleSubmit}>
          {accountValueProps && (
            <SignupValueProps valueProps={accountValueProps} />
          )}
          <StyledFields>
            <StyledFormRow>
              <FieldInput
                {...{ ...formFields.firstName, label: firstNameLabelText }}
              />

              <FieldInput
                {...{ ...formFields.lastName, label: lastNameLabelText }}
              />
            </StyledFormRow>

            <FieldInput {...{ ...formFields.email, label: emailLabelText }} />

            {!!ageGate && (
              <>
                <StyledLabel>{birthdayLabelText}</StyledLabel>
                <StyledBirthdayRow>
                  <StyledColumn>
                    <FieldSelect
                      {...formFields.month}
                      options={MonthsOptions}
                      placeholder={monthLabelText}
                    />
                  </StyledColumn>
                  <StyledColumn>
                    <FieldSelect
                      {...formFields.day}
                      options={daysOptions}
                      placeholder={dayLabelText}
                    />
                  </StyledColumn>
                  <StyledColumn>
                    <FieldSelect
                      {...formFields.year}
                      options={yearsOptions}
                      placeholder={yearLabelText}
                    />
                  </StyledColumn>
                </StyledBirthdayRow>
              </>
            )}
            {/* end of age gate */}

            <PasswordInput
              {...{ ...formFields.password, label: passwordLabelText }}
              handlePasswordChange={handlePasswordChange}
              localization={passwordLocalization}
              validationLocalization={validationLocalization}
            />
          </StyledFields>

          <FieldCheckbox
            name="emailOptIn"
            label={<EmailOptIn customText={customText} location="signup" />}
          />
          <StyledDivider />

          <TermsAndConditions
            localization={tosLocalization}
            submitFailed={submitFailed}
            terms={getSortedTermsByLocation({
              location: "signup",
              terms: customText,
            })}
            type="optin"
            valid={!!values.terms}
          >
            <StyledButtonWrapper>
              <Button htmlType="submit" loading={loading} fullWidth>
                {submitText}
              </Button>
            </StyledButtonWrapper>
          </TermsAndConditions>
          {error && <StyledError>{error}</StyledError>}
        </StyledForm>
      )}
    />
  );
};
